import { DateFormat, DateUnit, LocalizedDateFormat, MonthNumber, WeekDay } from './model/date'
import { LocalDateTime } from './local-date-time'
import { DateTime } from 'luxon'
import { WeekNumbers } from 'luxon/src/datetime'
import { Temporal } from './temporal'
import { LocalTime } from './local-time'
import { assertNotNullOrUndefined } from '../tsUtils'
import { Nullable } from '../typesUtils'
import { Zone } from 'luxon/src/zone'
import { DEFAULT_LOCALE } from '../constants'

/**
 * Méthode permettant de manipuler uniquement la partie date d'un {@link Date} js. <br>
 * Cette classe est sérializable, donc elle peut directement être envoyée au back.
 */
export class LocalDate extends Temporal {

	private constructor(temporal: DateTime) {
		super(temporal.startOf('day'))
	}

	public static now = (): LocalDate => new LocalDate(DateTime.now().setZone(Temporal.API_TIMEZONE))

	public static epoch = (): LocalDate => new LocalDate(DateTime.fromSeconds(0, { zone: Temporal.API_TIMEZONE }))

	public static fromAPI(temporal: string): LocalDate
	public static fromAPI(temporal: undefined): undefined
	public static fromAPI(temporal: null): null
	public static fromAPI(temporal: string | undefined): LocalDate | undefined
	public static fromAPI(temporal: string | null): LocalDate | null
	public static fromAPI(temporal: Nullable<string>): Nullable<LocalDate>
	public static fromAPI(temporal: Nullable<string>): Nullable<LocalDate> {
		if (assertNotNullOrUndefined(temporal)) {
			return new LocalDate(DateTime.fromISO(temporal, { zone: Temporal.API_TIMEZONE }))
		}
		return temporal
	}

	public static fromJS = (date: Date): LocalDate => new LocalDate(DateTime.fromJSDate(date, { zone: Temporal.API_TIMEZONE }))

	public static fromJSWithTimezone = (date: Date, timezone: string | Zone): LocalDate => new LocalDate(DateTime.fromJSDate(date, { zone: timezone }))

	public static fromString = (date: string, format = 'yyyy/MM/dd') => new LocalDate(DateTime.fromFormat(date, format, { zone: Temporal.API_TIMEZONE }))

	public format(): string
	public format(dateFormat: LocalizedDateFormat | DateFormat): string
	public format(dateFormat: LocalizedDateFormat | DateFormat = DateFormat.SHORT_DATE_2_DIGITS, locale?: string): string {
		return this.toDisplayDate(dateFormat, locale ?? DEFAULT_LOCALE)
	}

	public endOfDay = (): LocalDateTime => new LocalDateTime(this._date.endOf('day'))

	public startOfDay = (): LocalDateTime => new LocalDateTime(this._date.startOf('day'))

	public plus(amount: number, unit: DateUnit = DateUnit.day): LocalDate {
		return new LocalDate(this._date.plus({ [unit]: amount }))
	}

	public atTime(time: LocalTime): LocalDateTime {
		return this.atTimeString(time.toJSON())
	}

	public atTimeString(time: string): LocalDateTime {
		return new LocalDateTime(this._date.set(Temporal._splitTime(time)))
	}

	public minus(amount: number, unit: DateUnit = DateUnit.day): LocalDate {
		return this.plus(-amount, unit)
	}

	public startOf(unit: DateUnit = DateUnit.day): LocalDate {
		return new LocalDate(this._date.startOf(unit))
	}

	public endOf(unit: DateUnit = DateUnit.day): LocalDate {
		return new LocalDate(this._date.endOf(unit))
	}

	get weekNumber(): WeekNumbers {
		return this._safe(this._date).weekNumber
	}

	get dayOfMonth() {
		return this.toDisplayDate(DateFormat.SHORT_DATE_2_DIGITS_ONLY_DAY)
	}

	get weekday(): WeekDay {
		return this._date.weekday - 1 as never
	}

	get year(): number {
		return this._date.year
	}

	get month(): MonthNumber {
		return this._date.month - 1 as never
	}

	public toJSON(): string {
		return this._date.toISODate() as string
	}
}
